import { UseQueryOptions, useQuery } from '@tanstack/react-query'

import {
  EChatV2ServerPath,
  TLatestFormSubmissionResponse,
} from '~/features/chatbotV2/models'
import { guestChatGet, queryKeys } from '~/features/chatbotV2/utils'
import { store } from '~/store'

type TParams = {
  guestToken: string | null
  refreshGuestToken?: () => Promise<string | null>
  queryOptions?: Partial<UseQueryOptions<TLatestFormSubmissionResponse, Error>>
}

const getLatestFormSubmission = async (
  guestToken: string,
  refreshToken?: () => Promise<string | null>,
) =>
  guestChatGet<TLatestFormSubmissionResponse>({
    guestToken,
    path: EChatV2ServerPath.formSubmissionsLatest,
    refreshToken,
  })

const useGetLatestFormSubmission = ({
  guestToken,
  refreshGuestToken,
  queryOptions,
}: TParams) => {
  const agentId = store((state) => state.chatbot.agent.id) ?? ''
  const { enabled: optionsEnabled, ...restOptions } = queryOptions ?? {}

  return useQuery({
    // Guest JWT from POST /token is enough — heyLibbyChat's GET
    // /chat/form-submissions/latest keys off chatAuth claims only, not WS
    // SESSION_READY (see chat-service chatRoutes.ts + chatController.ts).
    enabled: !!agentId && !!guestToken && (optionsEnabled ?? true),
    // Deliberately NOT keyed on guestToken/refreshGuestToken — see
    // queryKeys.latestFormSubmission's own comment: a guest JWT rotates
    // within the same session (reconnects, and refreshGuestToken's self-heal
    // on a 401), and that must not look like cache-key data to react-query.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKeys.latestFormSubmission({ agentId }),
    queryFn: () => getLatestFormSubmission(guestToken!, refreshGuestToken),
    retry: false,
    refetchOnWindowFocus: false,
    ...restOptions,
  })
}

export default useGetLatestFormSubmission
